import { create } from 'zustand';
import { salesforceClient, isSalesforceRateLimited } from '../lib/salesforce';
import type { WebshopInpakkenOrder } from '../lib/salesforce';

// 2 min: inpak-orders veranderen niet per 30s, en dit scherm staat de hele dag
// open bij de wasserij — een kortere interval is onnodige SF-belasting.
const POLLING_INTERVAL_MS = 120000;

const MOCK_ORDERS: WebshopInpakkenOrder[] = [
  {
    orderId: 'a0mock000000001',
    orderName: 'WO-MOCK-001',
    customerName: 'Test Klant 1',
    street: 'Teststraat 1',
    postalCode: '1234 AB',
    city: 'Amsterdam',
    country: 'NL',
    trackingNumber: '3SKLED000000001',
    notes: null,
    createdDate: new Date().toISOString(),
    items: [
      {
        itemId: 'i0mock000000001',
        productName: 'Vlekkenbaas 400ml',
        quantity: 1,
        price: 12.95,
        width: null,
        length: null,
      },
    ],
  },
  {
    orderId: 'a0mock000000002',
    orderName: 'WO-MOCK-002',
    customerName: 'Test Klant 2',
    street: 'Voorbeeldlaan 42',
    postalCode: '5678 CD',
    city: 'Rotterdam',
    country: 'NL',
    trackingNumber: '3SKLED000000002',
    notes: 'Graag met de hand overhandigen',
    createdDate: new Date().toISOString(),
    items: [
      {
        itemId: 'i0mock000000002',
        productName: 'Kleda Base Ondertapijt',
        quantity: 1,
        price: 89,
        width: 200,
        length: 300,
      },
      {
        itemId: 'i0mock000000003',
        productName: 'Rena Vezelbeschermer Eco',
        quantity: 1,
        price: 24.5,
        width: null,
        length: null,
      },
    ],
  },
];

interface InpakkenStore {
  orders: WebshopInpakkenOrder[];
  isLoading: boolean;
  isRefreshing: boolean;
  error: string | null;
  pollingTimer: ReturnType<typeof setInterval> | null;

  fetchOrders: (showRefreshSpinner?: boolean) => Promise<void>;
  getOrderById: (orderId: string) => WebshopInpakkenOrder | undefined;
  getOrderByTracking: (trackingNumber: string) => Promise<WebshopInpakkenOrder | null>;
  removeOrder: (orderId: string) => WebshopInpakkenOrder | undefined;
  restoreOrder: (order: WebshopInpakkenOrder) => void;
  markPacked: (orderId: string) => Promise<{ success: boolean; message?: string }>;

  startPolling: () => void;
  stopPolling: () => void;
}

const isDevMockMode = import.meta.env.DEV;

export const useInpakkenStore = create<InpakkenStore>((set, get) => ({
  orders: [],
  isLoading: false,
  isRefreshing: false,
  error: null,
  pollingTimer: null,

  fetchOrders: async (showRefreshSpinner = false) => {
    if (showRefreshSpinner) {
      set({ isRefreshing: true });
    } else {
      set({ isLoading: true });
    }
    set({ error: null });

    try {
      const response = await salesforceClient.getInpakkenOrders();

      if (response.success && response.data) {
        set({ orders: response.data.orders ?? [] });
      } else {
        throw new Error(response.message || 'Kon orders niet ophalen');
      }
    } catch (err) {
      console.error('[InpakkenStore] fetchOrders failed:', err);

      if (isDevMockMode && get().orders.length === 0) {
        console.info('[InpakkenStore] DEV mock mode: using sample orders');
        set({ orders: MOCK_ORDERS });
      } else {
        set({ error: err instanceof Error ? err.message : 'Kon orders niet ophalen' });
      }
    } finally {
      set({ isLoading: false, isRefreshing: false });
    }
  },

  getOrderById: (orderId) => {
    return get().orders.find((o) => o.orderId === orderId);
  },

  getOrderByTracking: async (trackingNumber) => {
    const local = get().orders.find((o) => o.trackingNumber === trackingNumber);
    if (local) return local;

    try {
      const response = await salesforceClient.getInpakkenOrderByTracking(trackingNumber);
      if (response.success && response.data) {
        return response.data;
      }
      return null;
    } catch (err) {
      console.error('[InpakkenStore] getOrderByTracking failed:', err);
      return null;
    }
  },

  removeOrder: (orderId) => {
    const existing = get().orders.find((o) => o.orderId === orderId);
    if (!existing) return undefined;
    set({ orders: get().orders.filter((o) => o.orderId !== orderId) });
    return existing;
  },

  restoreOrder: (order) => {
    if (get().orders.some((o) => o.orderId === order.orderId)) return;
    set({ orders: [order, ...get().orders] });
  },

  markPacked: async (orderId) => {
    const isMockOrder = orderId.startsWith('a0mock');
    if (isDevMockMode && isMockOrder) {
      return { success: true };
    }

    try {
      const response = await salesforceClient.markOrderPacked(orderId);
      if (response.success) {
        return { success: true };
      }
      return { success: false, message: response.message || 'Kon order niet markeren' };
    } catch (err) {
      console.error('[InpakkenStore] markPacked failed:', err);
      return {
        success: false,
        message: err instanceof Error ? err.message : 'Kon order niet markeren',
      };
    }
  },

  startPolling: () => {
    const current = get().pollingTimer;
    if (current) return;
    const timer = setInterval(() => {
      // Circuit-breaker: niet pollen terwijl SF de daglimiet weigert.
      if (isSalesforceRateLimited()) return;
      get().fetchOrders(true);
    }, POLLING_INTERVAL_MS);
    set({ pollingTimer: timer });
  },

  stopPolling: () => {
    const current = get().pollingTimer;
    if (current) {
      clearInterval(current);
      set({ pollingTimer: null });
    }
  },
}));
